import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Users, UserPlus, Shield, Network, Plus, Minus } from "lucide-react";
import "./TeamManagement.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Testimonials from "../../components/Testimonials/Testimonials";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Footer from "../../components/Footer/Footer";

const TITLE = "Team Management Software with Roles and Permissions | TeamGrid";
const DESCRIPTION =
  "Manage roles, permissions and org hierarchy with complete control. Build structured teams with secure access and clear reporting lines.";
const CANONICAL = "https://teamgrid.ai/features/team-management";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const TEAL = "#00D1C1";
const ICON_BG = "#E0FFFC";
const CARD_BORDER = "#EDF4FF";

const features = [
  {
    icon: UserPlus,
    title: "Member Profiles",
    sub: "Complete info with roles, departments, and reporting structure",
  },
  {
    icon: Shield,
    title: "Permission Controls",
    sub: "Granular access controls for data and features",
  },
  {
    icon: Network,
    title: "Org Hierarchy",
    sub: "Visual org chart with reporting lines and team structure",
    image: "/Background+Border+Shadow_New_team.webp",
  },
];

const teamMembers = [
  {
    name: "Gaurav Prajapati",
    email: "gauravabc@gmail.com",
    initials: "GP",
    avatarColor: "#34A851",
    role: "Junior Marketing Designer",
    roleStyle: { bg: "#FFFFFF", border: "#EDF4FF", color: "#34A851" },
  },
  {
    name: "Deepika Dubey",
    email: "deepikadubeyxyz@gmail.com",
    initials: "DD",
    avatarColor: "#6C59A6",
    role: "BDE",
    roleStyle: { bg: "rgba(0,0,0,0.02)", border: "#F7F7F7", color: "#6C59A6" },
  },
  {
    name: "Purvi Joshi",
    email: "purvijoshixyz@gmail.com",
    initials: "PJ",
    avatarColor: "#F2673A",
    role: "Digital Marketing",
    roleStyle: {
      bg: "rgba(0,0,0,0.02)",
      border: "rgba(242,103,58,0.2)",
      color: "#F2673A",
    },
  },
];

const TeamManagement = () => {
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState({ 0: true });
  const toggleFaq = (i) => setFaqOpen((prev) => ({ ...prev, [i]: !prev[i] }));

  // FAQ data specific to Team Management — single Q&A
  const teamManagementFAQs = [
    {
      question: "What team control does Team Management allow?",
      answer:
        "Team Management provides admins with hierarchy controls, permission settings, visibility rules, and reporting options to manage visibility and analytics across teams.",
    },
  ];

  return (
    <div
      className="team-management-container"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={CANONICAL} />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <div className="relative min-h-screen overflow-hidden bg-white">
        <PublicHeader />
        <div className="pt-16 sm:pt-18 md:pt-20">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            {/* Hero: full-bleed gradient; content aligned with header */}
            <div
              className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen pt-16 sm:pt-18 md:pt-20 pb-10 sm:pb-12 md:pb-14 px-4 sm:px-6 lg:px-8"
              style={{
                background:
                  "radial-gradient(141.42% 141.42% at 0% 0%, rgba(230, 242, 255, 0.5) 0%, rgba(233, 252, 250, 0) 50%), radial-gradient(141.42% 141.42% at 100% 100%, rgba(240, 247, 255, 0.5) 0%, rgba(240, 247, 255, 0) 50%), #FFFFFF",
              }}
            >
              <div
                className="max-w-7xl mx-auto flex flex-col items-center text-center"
                style={{ gap: 20 }}
              >
                <div
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1"
                  style={{
                    background: "rgba(0, 209, 193, 0.06)",
                    borderColor: "rgba(0, 209, 193, 0.15)",
                  }}
                >
                  <Users
                    className="h-3 w-3 flex-shrink-0"
                    style={{ color: TEAL }}
                  />
                  <span
                    className="font-bold uppercase tracking-wide"
                    style={{
                      fontSize: 12,
                      lineHeight: "16px",
                      letterSpacing: "0.6px",
                      color: TEAL,
                    }}
                  >
                    Team Organization
                  </span>
                </div>
                <h1
                  className="font-extrabold text-black"
                  style={{
                    fontSize: "clamp(2.5rem, 5vw, 62px)",
                    lineHeight: "clamp(50px, 8vw, 72px)",
                  }}
                >
                  Team <span style={{ color: TEAL }}>Management</span>
                </h1>
                <p
                  className="mx-auto max-w-[768px] text-center px-4 sm:px-0"
                  style={{
                    fontSize: "clamp(16px, 4vw, 20px)",
                    lineHeight: "clamp(24px, 6vw, 28px)",
                    color: "rgba(0,0,0,0.6)",
                  }}
                >
                  Roles, permissions, and organizational hierarchy. Build and
                  manage your team structure with complete control.
                </p>
              </div>
            </div>

            {/* MAIN SECTION — padding 80px top/sides, reduced bottom, white */}
            <section
              className="flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8"
              style={{
                paddingTop: "clamp(40px, 8vw, 80px)",
                paddingBottom: "clamp(20px, 4vw, 40px)",
                paddingLeft: "clamp(20px, 4vw, 80px)",
                paddingRight: "clamp(20px, 4vw, 80px)",
                background: "#FFFFFF",
              }}
            >
              <div
                className="mb-6 flex w-full max-w-[1280px] flex-col gap-16 lg:flex-row lg:items-center lg:justify-center"
                style={{ gap: 64 }}
              >
                {/* Left: Feature list — 608px, gap 20px */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex flex-1 flex-col items-start lg:max-w-[608px]"
                  style={{ gap: 20 }}
                >
                  <h2
                    className="font-bold text-black"
                    style={{
                      fontSize: "40px",
                      lineHeight: "53px",
                      maxWidth: 608,
                    }}
                  >
                    Organize Your Team Structure
                  </h2>
                  <p
                    style={{
                      fontSize: 17,
                      lineHeight: "24px",
                      color: "rgba(0,0,0,0.6)",
                    }}
                  >
                    Create a clear organizational hierarchy with roles,
                    departments, and reporting lines. Manage permissions and
                    access levels for every team member.
                  </p>

                  <div
                    className="flex flex-col"
                    style={{ padding: "24px 0", gap: 32 }}
                  >
                    {features.map((f, i) => (
                      <div key={i} className="flex items-start gap-6">
                        {f.image ? (
                          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full overflow-hidden">
                            <img
                              src={f.image}
                              alt={f.title}
                              className="h-11 w-11 object-contain"
                            />
                          </div>
                        ) : (
                          <div
                            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border-4 border-white"
                            style={{
                              background: ICON_BG,
                              boxShadow:
                                "0px 0px 0px 2px rgba(0, 209, 193, 0.5)",
                            }}
                          >
                            <f.icon
                              className="h-5 w-5"
                              style={{ color: TEAL }}
                            />
                          </div>
                        )}
                        <div className="flex flex-col" style={{ gap: 7 }}>
                          <h3
                            className="font-bold text-black"
                            style={{ fontSize: 15, lineHeight: "28px" }}
                          >
                            {f.title}
                          </h3>
                          <p
                            style={{
                              fontSize: 15,
                              lineHeight: "23px",
                              color: "rgba(0,0,0,0.6)",
                            }}
                          >
                            {f.sub}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate("/book-demo")}
                    className="rounded-xl font-bold text-black w-full sm:w-auto"
                    style={{
                      padding: "12px 32px",
                      background: TEAL,
                      boxShadow: "0px 0px 15px rgba(0, 209, 193, 0.3)",
                      fontSize: 16,
                      lineHeight: "24px",
                    }}
                  >
                    Manage Team
                  </motion.button>
                </motion.div>

                {/* Right: 4. TEAM MANAGEMENT card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="flex flex-1 flex-col lg:max-w-[608px]"
                  style={{
                    background: "#FFFFFF",
                    border: `1px solid ${CARD_BORDER}`,
                    boxShadow: "0px 20px 40px -5px rgba(0, 0, 0, 0.1)",
                    borderRadius: 32,
                  }}
                >
                  <div className="flex flex-col gap-1 p-6">
                    <div className="flex items-center gap-2">
                      <Users
                        className="h-5 w-5 flex-shrink-0"
                        style={{ color: TEAL }}
                      />
                      <h3
                        className="font-bold text-black"
                        style={{ fontSize: 20, lineHeight: "28px" }}
                      >
                        Team Management
                      </h3>
                    </div>
                    <p
                      style={{
                        fontSize: 12,
                        lineHeight: "16px",
                        fontWeight: 500,
                        color: "rgba(0,0,0,0.6)",
                      }}
                    >
                      32 Total Members • 23 Positions
                    </p>
                  </div>

                  <div className="flex flex-col gap-4 px-6 pb-6">
                    {teamMembers.map((member, idx) => (
                      <div
                        key={idx}
                        className="flex flex-row items-center gap-4 rounded-2xl border bg-white p-4"
                        style={{
                          borderColor: CARD_BORDER,
                          boxShadow:
                            "0px 1px 3px rgba(0, 0, 0, 0.05), 0px 1px 2px rgba(0, 0, 0, 0.02)",
                        }}
                      >
                        <div
                          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-base font-bold text-white"
                          style={{ background: member.avatarColor }}
                        >
                          {member.initials}
                        </div>
                        <div className="flex flex-1 flex-col gap-0.5 min-w-0">
                          <span
                            className="font-bold text-black"
                            style={{ fontSize: 14, lineHeight: "20px" }}
                          >
                            {member.name}
                          </span>
                          <span
                            style={{
                              fontSize: 11,
                              lineHeight: "16px",
                              color: "#666666",
                            }}
                          >
                            {member.email}
                          </span>
                          <span
                            className="mt-1 inline-block w-fit rounded-full px-2 py-0.5 font-bold"
                            style={{
                              fontSize: 10,
                              lineHeight: "15px",
                              background: member.roleStyle.bg,
                              border: `1px solid ${member.roleStyle.border}`,
                              color: member.roleStyle.color,
                            }}
                          >
                            {member.role}
                          </span>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1.5">
                          <div className="h-1.5 w-1.5 rounded-full bg-[#34A851]" />
                          <span
                            className="font-bold"
                            style={{
                              fontSize: 11,
                              lineHeight: "16px",
                              color: "rgba(0,0,0,0.6)",
                            }}
                          >
                            Activated
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </section>

            {/* Testimonials Section */}
            <Testimonials />

            {/* FAQ Section - Light Grey Background (full-bleed left/right) */}
            <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen py-16 px-4 sm:px-6 md:px-8 bg-[#F3F4F6]">
              <div className="max-w-4xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center mb-12"
                >
                  <span className="inline-block px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wide text-[#0D9488] bg-[#CCFBF1] border border-[#99F6E4] mb-6">
                    GOT QUESTIONS?
                  </span>
                  <h2 className="text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] font-bold text-gray-900 mb-4 max-w-[900px] mx-auto">
                    Frequently Asked Questions
                  </h2>
                  <p className="text-lg text-gray-600">
                    Everything you need to know about how TeamGrid works and the
                    value it delivers.
                  </p>
                </motion.div>

                <div className="space-y-4 w-full max-w-[720px] mx-auto">
                  {teamManagementFAQs.map((faq, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="w-full rounded-xl p-6 cursor-pointer bg-white"
                      style={{
                        border: faqOpen[index]
                          ? "1px solid #28B4AE"
                          : "1px solid #D1D5DB",
                        transition: "border-color 0.15s ease-in-out",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h3
                          className="text-left font-bold text-[#1A1A1A] flex-1"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            lineHeight: "26px",
                          }}
                        >
                          {faq.question}
                        </h3>
                        <span className="flex-shrink-0">
                          {faqOpen[index] ? (
                            <Minus className="w-5 h-5 text-[#111111]" />
                          ) : (
                            <Plus className="w-5 h-5 text-[#111111]" />
                          )}
                        </span>
                      </button>
                      <AnimatePresence>
                        {faqOpen[index] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <p
                              className="mt-4"
                              style={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 400,
                                fontSize: 16,
                                lineHeight: "26px",
                                color: "#374151",
                              }}
                            >
                              {faq.answer}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            <ReadyToTransform />

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamManagement;
